/**
 * Remembers where the user was heading when they got bounced to the login screen, so that after
 * logging in they land there instead of on their default product list.
 *
 * Why this exists: App.tsx renders the login screen for ANY path when there is no token, and
 * login.tsx then navigates to the first product the user has access to. Without a stash, an ERP
 * deep link silently degrades into "you are now on some list, go find it yourself".
 *
 * Scope is ONE route family, /go/ — see isRestorable for why it is not "any page".
 *
 * sessionStorage, not router state: the bounce happens from more than one place (the
 * unauthenticated catch-all, and the resolver's own 401 handler), and router state would have to
 * be threaded through each of them. It is also deliberately NOT localStorage — a stale target
 * left over from a previous browser session is noise, not intent.
 */

const KEY = 'postLoginRedirect';

/** Paths that must never be stashed: bouncing back to them after login is a loop, not a return. */
const isSelfDefeating = (path: string): boolean =>
  path.startsWith('/login') || path.startsWith('/forgotPassword');

/**
 * An in-app path is one leading slash followed by something that is not another slash.
 * "//evil.example" and "/\evil.example" are both read as protocol-relative URLs by the browser, so
 * either would turn a stashed value into an off-site redirect for a freshly-authenticated user.
 */
const isInAppPath = (path: string): boolean =>
  path.startsWith('/') && !path.startsWith('//') && !path.startsWith('/\\');

/**
 * Only ERP deep links are restorable. This is deliberately far narrower than "any page".
 *
 * The stash is written by the unauthenticated catch-all, and on React 17 (this project: 17.0.2,
 * legacy ReactDOM.render, no StrictMode) a setState inside a promise callback is NOT batched — so
 * every one of the ~30 `removeToken()` calls in 401 handlers re-renders App into the
 * unauthenticated tree synchronously, while the URL is still the page being left. Restoring any
 * path would therefore mean: partner A's session lapses on a shared installer tablet, installer B
 * logs in on that tab, and B is navigated to A's installation URL — a blank pane for B, with A's
 * installation id left in the address bar.
 *
 * Restricting it to /go/ keeps the one thing this feature actually requires (an ERP link survives
 * the login round trip) and gives every other 401 back its previous behaviour, which was to land
 * on the user's own default product list. Widening this again means first clearing the stash on
 * EVERY token removal, not just the logout button.
 */
const isRestorable = (path: string): boolean => path.startsWith('/go/');

/**
 * Query params that must never be persisted. App.tsx auto-logs-in from `?username=` and strips both
 * from the address bar immediately afterwards, i.e. the app already treats them as values that must
 * not linger — writing them into sessionStorage would put them straight back, for the life of the
 * tab. Nothing that is legitimately deep-linked needs them.
 */
const CREDENTIAL_PARAMS = ['username', 'reset'];

const withoutCredentials = (path: string): string => {
  const [base, query] = path.split('?');
  if (!query) return path;

  const params = new URLSearchParams(query);
  CREDENTIAL_PARAMS.forEach((p) => params.delete(p));
  const rest = params.toString();
  return rest === '' ? base : `${base}?${rest}`;
};

export const stashRedirect = (path: string): void => {
  if (!isInAppPath(path) || isSelfDefeating(path) || !isRestorable(path)) return;
  try {
    sessionStorage.setItem(KEY, withoutCredentials(path));
  } catch {
    // Private-mode / storage-disabled browsers: losing the destination is a degraded landing,
    // not a broken login. Fall through silently.
  }
};

/**
 * Drops any stashed destination without consuming it as a target.
 *
 * Belt and braces since isRestorable landed: logging out from /go/{docname} itself is the only way
 * the catch-all can still stash on the way out, and this clears it so the next person to log in on
 * the same tab does not inherit that destination.
 * ⚠ Deliberately NOT wired into deleteToken: ErpLinkResolver's 401 bounce stashes on purpose and
 * then removes the token, so clearing there would defeat the whole feature.
 */
export const clearRedirect = (): void => {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    // Storage disabled: there was nothing to clear.
  }
};

/**
 * Returns the stashed path and clears it, so a destination is consumed exactly once — a second
 * login in the same tab must not replay an old target.
 */
export const takeRedirect = (): string | null => {
  try {
    const path = sessionStorage.getItem(KEY);
    sessionStorage.removeItem(KEY);
    // Re-validated on read, not just on write: the value could have been hand-edited in
    // sessionStorage, and a stash written by an older deployed bundle may predate isRestorable.
    if (
      path === null ||
      !isInAppPath(path) ||
      isSelfDefeating(path) ||
      !isRestorable(path)
    )
      return null;
    return path;
  } catch {
    return null;
  }
};
